export interface Pt {
  lat: number;
  lng: number;
}

export function haversineMi(a: Pt, b: Pt): number {
  const R = 3958.8;
  const toR = (d: number) => (d * Math.PI) / 180;
  const dLat = toR(b.lat - a.lat);
  const dLng = toR(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Greedy nearest-neighbor visit order from `start`. Good enough for a Saturday loop. */
export function orderStops<T extends Pt & { id: string }>(start: Pt, stops: T[]): string[] {
  const left = stops.slice();
  const out: string[] = [];
  let cur: Pt = start;
  while (left.length) {
    let bi = 0;
    let bd = Infinity;
    left.forEach((s, i) => {
      const d = haversineMi(cur, s);
      if (d < bd) {
        bd = d;
        bi = i;
      }
    });
    const next = left.splice(bi, 1)[0]!;
    out.push(next.id);
    cur = next;
  }
  return out;
}

/** The N closest candidates to `origin`, nearest first. Used to "sprinkle" nearby places into a
 * route without changing its overall shape. */
export function nearestStops<T extends Pt & { id: string }>(origin: Pt, candidates: T[], n: number): T[] {
  return candidates
    .map((c) => ({ c, d: haversineMi(origin, c) }))
    .sort((a, b) => a.d - b.d)
    .slice(0, Math.max(0, n))
    .map((x) => x.c);
}

/**
 * Google Maps directions URLs, one per leg. Google accepts at most 9
 * waypoints in a URL, so each leg carries up to 9 intermediate stops plus a
 * destination; the next leg starts where the last one ended.
 */
export function googleMapsLegs(start: Pt, ordered: Pt[], maxWaypoints = 9): string[] {
  const fmt = (p: Pt) => `${+p.lat.toFixed(5)},${+p.lng.toFixed(5)}`;
  const legs: string[] = [];
  let origin = start;
  let i = 0;
  while (i < ordered.length) {
    const chunk = ordered.slice(i, i + maxWaypoints + 1);
    const dest = chunk[chunk.length - 1]!;
    const wps = chunk.slice(0, -1);
    const u = new URL('https://www.google.com/maps/dir/');
    u.searchParams.set('api', '1');
    u.searchParams.set('origin', fmt(origin));
    u.searchParams.set('destination', fmt(dest));
    u.searchParams.set('travelmode', 'driving');
    if (wps.length) u.searchParams.set('waypoints', wps.map(fmt).join('|'));
    legs.push(u.toString());
    origin = dest;
    i += chunk.length;
  }
  return legs;
}
